import {SidebarAbout} from '@github-ui/code-view-sidebar/components/SidebarAbout'
import {SidebarContributorsSkeleton} from '@github-ui/code-view-sidebar/components/SidebarContributors'
import {SidebarDeploymentsSkeleton} from '@github-ui/code-view-sidebar/components/SidebarDeployments'
import {SidebarLanguagesSkeleton} from '@github-ui/code-view-sidebar/components/SidebarLanguages'
import {SidebarPackagesSkeleton} from '@github-ui/code-view-sidebar/components/SidebarPackages'
import {SidebarReleasesSkeleton} from '@github-ui/code-view-sidebar/components/SidebarReleases'
import {SidebarSponsorsSkeleton} from '@github-ui/code-view-sidebar/components/SidebarSponsors'
import {SidebarUsedBySkeleton} from '@github-ui/code-view-sidebar/components/SidebarUsedBy'
import {CodeViewSidebarLayout} from '@github-ui/code-view-sidebar/layout'
import type {LicenseInfo} from '@github-ui/code-view-sidebar/types'
import {type DataRouterFileOverviewPayload, PreferredFileTypes} from '@github-ui/code-view-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQueryClient} from '@github-ui/react-query'
import {Suspense, useCallback, useMemo} from 'react'

import {codeViewRepoRoute} from '../../routes/code-view-repo-route'
import {SidebarContributors} from './sidebar/SidebarContributors'
import {SidebarDeployments} from './sidebar/SidebarDeployments'
import {SidebarLanguages} from './sidebar/SidebarLanguages'
import {SidebarPackages} from './sidebar/SidebarPackages'
import {SidebarReleases} from './sidebar/SidebarReleases'
import {SidebarSponsors} from './sidebar/SidebarSponsors'
import {SidebarSuggestedWorkflows} from './sidebar/SidebarSuggestedWorkflows'
import {SidebarUsedBy} from './sidebar/SidebarUsedBy'

/** Data extracted from overviewFiles in the main payload. */
export interface OverviewFilesData {
  license?: LicenseInfo
  hasReadme: boolean
  hasCodeOfConduct: boolean
  hasContributing: boolean
  hasSecurityPolicy: boolean
}

export type OverviewSidebarProps = {
  overview: Pick<DataRouterFileOverviewPayload['overview'], 'overviewFiles'>
}

/**
 * Extract sidebar-relevant data from overviewFiles.
 *
 * Reuses tree_payload_helper's proven resolution so we don't need
 * duplicate fields in the sidebarAbout endpoint.
 */
function extractOverviewFilesData({overviewFiles}: OverviewSidebarProps['overview']): OverviewFilesData | undefined {
  return overviewFiles?.reduce((acc, file) => {
    if (file.preferredFileType === PreferredFileTypes.LICENSE) {
      acc.license = {name: file.tabName ?? 'License'}
    } else if (file.preferredFileType === PreferredFileTypes.README) {
      acc.hasReadme = true
    } else if (file.preferredFileType === PreferredFileTypes.CODE_OF_CONDUCT) {
      acc.hasCodeOfConduct = true
    } else if (file.preferredFileType === PreferredFileTypes.CONTRIBUTING) {
      acc.hasContributing = true
    } else if (file.preferredFileType === PreferredFileTypes.SECURITY) {
      acc.hasSecurityPolicy = true
    }
    return acc
  }, {} as OverviewFilesData)
}

function OverviewSidebarSection({
  children,
  fallback,
  errorFallback = null,
}: {
  children: React.ReactNode
  fallback: React.ReactNode
  errorFallback?: React.ReactNode | null
}) {
  return (
    <ErrorBoundary fallback={errorFallback}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}

/**
 * Sidebar orchestrator for the repository overview page.
 *
 * Uses a blocking `sidebarAbout` query for the About section so it renders
 * immediately. Each additional section uses `useSuspenseQuery` inside a
 * `<Suspense>` boundary — sections only fetch when they render (gated by
 * the `sections` config from about), avoiding unnecessary requests.
 */
export function OverviewSidebar({overview}: OverviewSidebarProps) {
  const reactSidebarEnabled = isFeatureEnabled('react_data_router_code_view_sidebar')
  const overviewFiles = useMemo(() => extractOverviewFilesData(overview), [overview])
  const queryClient = useQueryClient()
  const {data: aboutData, queryKey} = useRouteQuery(codeViewRepoRoute, 'sidebarAbout', {
    enabled: reactSidebarEnabled,
  })

  const invalidateAboutQuery = useCallback(() => {
    return queryClient.invalidateQueries({queryKey})
  }, [queryKey, queryClient])

  if (!aboutData || !overviewFiles || !reactSidebarEnabled) {
    return null
  }

  const {sections, ownerLogin, repoName} = aboutData

  return (
    <CodeViewSidebarLayout>
      <SidebarAbout
        className="hide-sm hide-md"
        {...aboutData}
        {...overviewFiles}
        onEditDialogSaved={invalidateAboutQuery}
      />

      {sections.releases && (
        <OverviewSidebarSection fallback={<SidebarReleasesSkeleton />}>
          <SidebarReleases ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.sponsors && (
        <OverviewSidebarSection fallback={<SidebarSponsorsSkeleton />}>
          <SidebarSponsors ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.deployments && (
        <OverviewSidebarSection fallback={<SidebarDeploymentsSkeleton />}>
          <SidebarDeployments ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.packages && (
        <OverviewSidebarSection fallback={<SidebarPackagesSkeleton />}>
          <SidebarPackages ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.usedBy && (
        <OverviewSidebarSection fallback={<SidebarUsedBySkeleton />}>
          <SidebarUsedBy ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.contributors && (
        <OverviewSidebarSection fallback={<SidebarContributorsSkeleton />}>
          <SidebarContributors ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      {sections.languages && (
        <OverviewSidebarSection fallback={<SidebarLanguagesSkeleton />}>
          <SidebarLanguages ownerLogin={ownerLogin} repoName={repoName} />
        </OverviewSidebarSection>
      )}

      <OverviewSidebarSection fallback={null}>
        <SidebarSuggestedWorkflows ownerLogin={ownerLogin} repoName={repoName} />
      </OverviewSidebarSection>
    </CodeViewSidebarLayout>
  )
}
